import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../lib/auth/AuthContext';
import { Id } from '../../convex/_generated/dataModel';
import PasswordChangeModal from './PasswordChangeModal';

export const UserManagement = () => {
  const { user, useAuthenticatedQuery, useAuthenticatedMutation } = useAuth();
  const users = useAuthenticatedQuery('users:listUsers', {}) || [];
  const roles = useAuthenticatedQuery('roles:getRoles', {}) || [];
  const createUser = useAuthenticatedMutation('auth:createUser');
  const updateUser = useAuthenticatedMutation('users:updateUser');

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    roleId: '',
  });

  const [passwordChangeModal, setPasswordChangeModal] = useState<{
    isOpen: boolean;
    user: any | null;
  }>({
    isOpen: false,
    user: null,
  });

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    try {
      await createUser({
        ...newUser,
        roleId: newUser.roleId as Id<"roles">,
      });
      setNewUser({ name: '', email: '', password: '', roleId: '' });
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const handleStatusChange = async (userId: Id<"users">, status: string) => {
    try {
      await updateUser({ 
        userId, 
        updates: {
          status
        }
      });
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };

  const handleRoleChange = async (userId: Id<"users">, roleId: Id<"roles">) => {
    try {
      await updateUser({ 
        userId, 
        updates: {
          roleId
        }
      });
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const handleChangePassword = (user: any) => {
    setPasswordChangeModal({
      isOpen: true,
      user: user,
    });
  };

  const handlePasswordChangeSuccess = () => {
    // You could add a toast notification here
    alert('Password changed successfully!');
  };

  if (!user?.role?.permissions?.includes('manage_users')) {
    return <div>You don't have permission to manage users.</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">User Management</h2>
      
      {/* Create User Form */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-xl font-semibold mb-4">Create New User</h3>
        <form onSubmit={handleCreateUser} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              placeholder="Name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
            />
            <Input
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
            <Input
              type="password"
              placeholder="Password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            />
            <select
              className="border rounded-md p-2"
              value={newUser.roleId}
              onChange={(e) => setNewUser({ ...newUser, roleId: e.target.value })}
            >
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.displayName}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit">Create User</Button>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user._id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <select
                    className="border rounded-md p-1"
                    value={user.roleId}
                    onChange={(e) => handleRoleChange(user._id, e.target.value as Id<"roles">)}
                  >
                    {roles.map((role) => (
                      <option key={role._id} value={role._id}>
                        {role.displayName}
                      </option>
                    ))}
                  </select>
                </TableCell>
                <TableCell>
                  <select
                    className="border rounded-md p-1"
                    value={user.status}
                    onChange={(e) => handleStatusChange(user._id, e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="locked">Locked</option>
                  </select>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(user._id, user.status === 'active' ? 'inactive' : 'active')}
                    >
                      {user.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleChangePassword(user)}
                    >
                      Change Password
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={passwordChangeModal.isOpen}
        user={passwordChangeModal.user}
        onClose={() => setPasswordChangeModal({ isOpen: false, user: null })}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
}; 